/* ShinyText — React Bits (JS-CSS variant), ported to a dependency-free custom element.
 * Source: https://reactbits.dev/r/ShinyText-JS-CSS.json
 * The upstream component renders a motion.span driven by `motion/react`. This project
 * ships as plain HTML/CSS/JS with no bundler, so the same maths (gradient formula,
 * progress cycle, yoyo, delay, direction, pause-on-hover) is reproduced on rAF.
 *
 * <shiny-text text="Qore Ultra" speed="2" delay="0" color="#b5b5b5"
 *             shine-color="#ffffff" spread="120" direction="left"
 *             yoyo pause-on-hover disabled></shiny-text>
 */
(function () {
  'use strict';
  if (customElements.get('shiny-text')) return;

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* The upstream component sweeps background-position 150% -> -50% linearly over
   * `speed` seconds. With no delay/yoyo/pause that is expressible as a CSS keyframe,
   * which keeps running when rAF is throttled and costs nothing on the main thread.
   * The rAF path below still handles delay, yoyo and pause-on-hover. */
  if (!document.getElementById('shiny-text-keyframes')) {
    var sheet = document.createElement('style');
    sheet.id = 'shiny-text-keyframes';
    sheet.textContent =
      '@keyframes shiny-text-sweep { from { background-position: 150% center; } to { background-position: -50% center; } }' +
      '@media (prefers-reduced-motion: reduce) { shiny-text { animation: none !important; background-position: 50% center !important; } }';
    document.head.appendChild(sheet);
  }

  class ShinyText extends HTMLElement {
    static get observedAttributes() {
      return ['text', 'speed', 'delay', 'color', 'shine-color', 'spread', 'direction', 'yoyo', 'pause-on-hover', 'disabled'];
    }

    constructor() {
      super();
      this._elapsed = 0;
      this._last = null;
      this._paused = false;
      this._raf = 0;
    }

    /* ---- props ---- */
    get _speed() { return parseFloat(this.getAttribute('speed')) || 2; }
    get _delay() { return parseFloat(this.getAttribute('delay')) || 0; }
    get _color() { return this.getAttribute('color') || '#b5b5b5'; }
    get _shine() { return this.getAttribute('shine-color') || '#ffffff'; }
    get _spread() { return parseFloat(this.getAttribute('spread')) || 120; }
    get _dir() { return this.getAttribute('direction') === 'right' ? -1 : 1; }
    get _yoyo() { return this.hasAttribute('yoyo') && this.getAttribute('yoyo') !== 'false'; }
    get _hoverPause() { return this.hasAttribute('pause-on-hover') && this.getAttribute('pause-on-hover') !== 'false'; }
    get _disabled() { return this.hasAttribute('disabled') && this.getAttribute('disabled') !== 'false'; }

    connectedCallback() {
      if (!this._wired) {
        this._wired = true;
        this._onEnter = function () { if (this._hoverPause) this._paused = true; }.bind(this);
        this._onLeave = function () { if (this._hoverPause) this._paused = false; }.bind(this);
        this.addEventListener('mouseenter', this._onEnter);
        this.addEventListener('mouseleave', this._onLeave);
      }
      this._paint();
      if (!reduce && !this._cssDrivable()) this._start();
    }

    disconnectedCallback() {
      cancelAnimationFrame(this._raf);
      this._raf = 0;
      this._last = null;
    }

    attributeChangedCallback(name) {
      if (name === 'direction') { this._elapsed = 0; }
      this._paint();
      if (this.isConnected && !reduce && !this._cssDrivable()) this._start();
    }

    /* ---- render ---- */
    _paint() {
      var label = this.getAttribute('text');
      if (label !== null && this.textContent !== label) this.textContent = label;
      this.style.display = 'inline-block';
      /* Poppins Q draws a tail below the em box. background-clip:text clips
         that tail unless the padding box is taller than the glyph. */
      this.style.lineHeight = '1.25';
      this.style.padding = '0.06em 0.04em 0.28em 0.12em';
      this.style.margin = '0';
      this.style.overflow = 'visible';
      this.style.backgroundImage = 'linear-gradient(' + this._spread + 'deg, ' +
        this._color + ' 0%, ' + this._color + ' 40%, ' + this._shine + ' 50%, ' +
        this._color + ' 60%, ' + this._color + ' 100%)';
      this.style.backgroundSize = '200% auto';
      this.style.webkitBackgroundClip = 'text';
      this.style.backgroundClip = 'text';
      this.style.webkitTextFillColor = 'transparent';
      if (reduce || this._disabled) {
        this.style.animation = 'none';
        this.style.backgroundPosition = '50% center';
        return;
      }
      if (this._cssDrivable()) {
        this.style.animation = 'shiny-text-sweep ' + this._speed + 's linear infinite' +
          (this._dir === 1 ? '' : ' reverse');
      } else {
        this.style.animation = 'none';
      }
    }

    /* background-clip:text does not repaint when background-position is
       animated in CSS, so the sweep is always driven from script. */
    _cssDrivable() {
      return false;
    }

    _start() {
      if (this._raf) return;
      var self = this;
      var tick = function (time) {
        self._raf = requestAnimationFrame(tick);

        if (self._disabled || self._paused) { self._last = null; return; }
        if (self._last === null) { self._last = time; return; }

        self._elapsed += time - self._last;
        self._last = time;

        var anim = self._speed * 1000;
        var wait = self._delay * 1000;
        var cycle = anim + wait;
        var d = self._dir;
        var p;

        if (self._yoyo) {
          var full = cycle * 2;
          var ct = self._elapsed % full;
          if (ct < anim) {
            p = (ct / anim) * 100;
            p = d === 1 ? p : 100 - p;
          } else if (ct < cycle) {
            p = d === 1 ? 100 : 0;
          } else if (ct < cycle + anim) {
            var rt = ct - cycle;
            var q = 100 - (rt / anim) * 100;
            p = d === 1 ? q : 100 - q;
          } else {
            p = d === 1 ? 0 : 100;
          }
        } else {
          var t = self._elapsed % cycle;
          if (t < anim) {
            p = (t / anim) * 100;
            p = d === 1 ? p : 100 - p;
          } else {
            p = d === 1 ? 100 : 0;
          }
        }

        /* p=0 -> shine off right, p=100 -> shine off left */
        self.style.backgroundPosition = (150 - p * 2) + '% center';
      };
      this._raf = requestAnimationFrame(tick);
    }
  }

  customElements.define('shiny-text', ShinyText);
})();
